import type { ScoProduct } from '@src/graphql-types'

import type { PaywallTermOption } from './PaywallOfferControls'
import { TERM_MONTHS } from './presentation'

/** Billing terms a membership chooser offers, in display order. */
export const MEMBERSHIP_TERM_IDS = ['month', 'threeMonth', 'sixMonth', 'year'] as const

/** Short enough that four terms share one row on a phone. */
const TERM_LABELS: Record<string, string> = {
  month: 'Monthly',
  threeMonth: 'Quarterly',
  sixMonth: '6 months',
  year: 'Annual',
}

export function membershipTermLabel(termId: string): string {
  return TERM_LABELS[termId] || termId
}

/** Button copy for moving the same product onto another billing term. */
export function billingTermChangeLabel(
  direction: 'upgrade' | 'downgrade' | 'switch',
  termId: string
): string {
  const name = membershipTermLabel(termId)
  const readable = name === '6 months' ? name : name.toLowerCase()
  if (direction === 'downgrade') {
    return `Downgrade to ${readable}`
  }
  if (direction === 'switch') {
    return `Switch to ${readable}`
  }
  return `Upgrade to ${readable}`
}

/** Below this, a "save" badge reads as noise rather than a reason to prepay. */
const MIN_SAVINGS_PERCENT = 5

function termPrice(product: ScoProduct, termId: string) {
  const term = product.terms?.find(candidate => candidate?.termId === termId)
  const cents = term?.pricingInCents?.recurring ?? term?.pricingInCents?.signup
  return term && cents != null ? { cents, months: term.months ?? TERM_MONTHS[termId] ?? null } : null
}

/**
 * Whole-percent savings of prepaying `termId` versus paying monthly for the same months, from
 * the product's own catalog prices. Null when either price or the term length is missing.
 */
export function termSavingsPercent(product: ScoProduct, termId: string): number | null {
  const monthly = termPrice(product, 'month')
  const prepaid = termPrice(product, termId)
  if (!monthly || !prepaid?.months || termId === 'month' || monthly.cents <= 0) {
    return null
  }
  const fullPrice = monthly.cents * prepaid.months
  return Math.floor(((fullPrice - prepaid.cents) / fullPrice) * 100)
}

/**
 * Term options shared by every product on a chooser, each labeled with savings computed from
 * the catalog (never a fixed "Save 16%"). Different savings across plans read "Save up to N%".
 */
export function membershipTermOptions(products: ScoProduct[]): PaywallTermOption[] {
  if (products.length === 0) {
    return []
  }
  return MEMBERSHIP_TERM_IDS.filter(termId => products.every(product => termPrice(product, termId))).map(
    termId => {
      const savings = products
        .map(product => termSavingsPercent(product, termId))
        .filter((value): value is number => value != null)
      const lowest = savings.length === products.length ? Math.min(...savings) : null
      const highest = savings.length ? Math.max(...savings) : null
      let savingsLabel: string | undefined
      if (highest != null && highest >= MIN_SAVINGS_PERCENT) {
        savingsLabel = lowest === highest ? `Save ${highest}%` : `Save up to ${highest}%`
      }
      return { id: termId, label: TERM_LABELS[termId] || termId, savingsLabel }
    }
  )
}

/**
 * Prices a plan at `termId` from catalog terms already on the product, so a chooser can follow
 * the term control before that period's query returns. A coupon stays on the server-priced
 * product: catalog amounts would drop the discount. Identity (`id`) is left alone.
 */
export function productPricedAtTerm(product: ScoProduct, termId: string): ScoProduct {
  const viewed = product.effectiveTermId || product.termId
  if (!termId || viewed === termId || product.appliedCoupon?.code) {
    return product
  }
  const term = product.terms?.find(candidate => candidate?.termId === termId)
  const signup = term?.pricingInCents?.signup
  const recurring = term?.pricingInCents?.recurring
  if (!term?.termId || signup == null || recurring == null) {
    return product
  }
  return {
    ...product,
    termId: term.termId,
    effectiveTermId: term.termId,
    effectiveTerm: {
      abbreviation: term.abbreviation,
      months: term.months,
      name: term.name,
      termId: term.termId,
    },
    pricingInCents: { signup, recurring },
    effectivePricingInCents: { signup, recurring },
  }
}
